import { useMemo, useState } from 'react';
import { IContactForList } from '@nkr/models/index'
import { AgGridReact } from 'ag-grid-react'
import { BodyScrollEvent, ColDef, FirstDataRenderedEvent, GridReadyEvent, ICellRendererParams, IHeaderParams } from 'ag-grid-community'
import { GridWrapper } from '../../styles'
import { ActionButtonRenderer } from '../action-button-renderer'
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header'
import RankRenderer from '@nkr/views/admin/center/list/common/rank-renderer'
import Roles from '../roles'
import { Checkbox } from 'react-bootstrap'

interface IProps {
    data: IContactForList[],
    canHardDelete: boolean,
    selectedForBulkDelete: number[],
    onGridReady: (e: GridReadyEvent) => void,
    onFirstDataRendered: (e: FirstDataRenderedEvent) => void,
    onMenuSelection: (eventKey: number, contactId: number) => void,
    currentUser?: any,
    contacts?: IContactForList[],
    selectedContactIds?: number[],
    onSelectionChange?: (selectedIds: number[]) => void
}

const AdminGrid = (props: IProps) => {
    const [menuDisplayed, setMenuDisplayed] = useState(false);

    const handleMenuToggle = (show: boolean) => {
        setMenuDisplayed(show);
    };

    const onBodyScroll = (e: BodyScrollEvent) => {
        if (menuDisplayed) {
            document.body.click();
            setMenuDisplayed(false);
        }
    }

    const handleSelectContact = (contactId: number, checked: boolean) => {
        if (!props.onSelectionChange || !props.selectedContactIds) return;
        
        if (checked) {
            props.onSelectionChange([...props.selectedContactIds, contactId]);
        } else {
            props.onSelectionChange(props.selectedContactIds.filter(id => id !== contactId));
        }
    };

    const columns: ColDef[] = [
        {
            colId: 'Rank',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>R#</SortableHeaderComponent>,
            headerName: 'Rank',
            width: 55,
            sortable: false,
            cellRendererFramework: (params: ICellRendererParams) => <RankRenderer {...params} />
        },
        ...(props.canHardDelete ? [{
            colId: 'Status',
            headerName: 'Status',
            width: 100,
            cellClass: 'center-list-cell',
            headerClass: 'center-list-header',
            sortable: false,
            suppressMenu: true,
            suppressMovable: true,
            cellRendererFramework: (cellProps: ICellRendererParams) =>
                <span style={{ color: cellProps.data.isDeleted ? 'red' : 'green' }}>
                    {cellProps.data.isDeleted ? 'Deleted' : 'Active'}
                </span>
        }] : []),


        {
            colId: 'FirstName',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>First Name</SortableHeaderComponent>,
            headerName: 'First Name',
            field: 'firstName',
            width: 125,
            cellRendererFramework: (props: ICellRendererParams) =>
                <>
                    {props.value ? props.value : '--'}
                </>
            ,
            comparator: (valueA, valueB) => {
                return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
            }
        },
        {
            colId: 'LastName',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Last Name</SortableHeaderComponent>,
            headerName: 'Last Name',
            field: 'lastName',
            width: 125,
            cellRendererFramework: (props: ICellRendererParams) =>
                <>
                    {props.value ? props.value : '--'}
                </>
            ,
            comparator: (valueA, valueB) => {
                return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
            }
        },

        {
            colId: 'Email',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Email</SortableHeaderComponent>,
            headerName: 'Email',
            field: 'email',
            width: 350,
            cellRendererFramework: (props: ICellRendererParams) =>
                <>
                    {props.value ? props.value : '--'}
                </>
            ,
            comparator: (valueA, valueB) => {
                return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
            }
        },

        {
            colId: 'Entity',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Entity (Center, Swap Courier, etc)</SortableHeaderComponent>,
            headerName: 'Entity (Center, Swap Courier, etc)',
            field: 'entityName',
            width: 225,
        },

        {
            colId: 'Role',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Roles</SortableHeaderComponent>,
            headerName: 'Roles',
            field: 'positions',
            cellClass: 'center-list-cell',
            headerClass: 'center-list-header',
            sortable: false,
            suppressSizeToFit: false,
            cellRendererFramework: (props: ICellRendererParams) => <Roles {...props} />
        },
        ...(props.canHardDelete ? [{
            colId: 'Select',
            headerName: 'Select',
            width: 80,
            minWidth: 80,
            maxWidth: 70,
            sortable: false,
            resizable: false,
            suppressMenu: true,
            suppressMovable: true,
            cellClass: 'center-list-cell select-column-cell',
            headerClass: 'center-list-header select-column-header',
            cellRendererFramework: (params: ICellRendererParams) => (
                <Checkbox
                    checked={props.selectedContactIds?.includes(params.data.id) || false}
                    onClick={(e: any) => {
                        e.stopPropagation();
                        handleSelectContact(params.data.id, e.target.checked);
                    }}
                />
            )
        }] : []),
        {
            colId: 'Actions',
            headerName: 'Actions',
            cellClass: 'center-list-cell select-column-cell',
            headerClass: 'center-list-header select-column-header',
            minWidth: 60,
            maxWidth: 100,
            sortable: false,
            suppressSizeToFit: false,
            cellRendererFramework: (params: ICellRendererParams) =>
                <ActionButtonRenderer
                    {...params}
                    canHardDelete={props.canHardDelete}
                    canDelete={true}
                    onMenuToggle={handleMenuToggle}
                    onMenuSelection={props.onMenuSelection}
                    currentUser={props.currentUser} />
        },
    ];

    const columnDefs = useMemo(() => columns, [props.currentUser, props.selectedContactIds]);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <GridWrapper className='container-fluid ag-theme-bootstrap' style={{ height: '82vh' }}>
            <AgGridReact
                columnDefs={columnDefs}
                defaultColDef={{
                    cellClass: 'center-list-cell',
                    headerClass: 'center-list-header',
                    sortable: true,
                    suppressSizeToFit: true,
                }}
                rowData={rowData}
                rowHeight={30}
                enableCellTextSelection={true}
                noRowsOverlayComponentFramework={() => <div>No contacts found...</div>}
                onGridReady={props.onGridReady}
                onFirstDataRendered={props.onFirstDataRendered}
                onBodyScroll={onBodyScroll}
            >

            </AgGridReact>
            <div style={{ marginTop: 5 }}>{props.data.length} contacts...</div>
        </GridWrapper>
    );
}

export default AdminGrid;